import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forumApi } from '../api/forum';
import { filesApi } from '../api/files';
import { UserContext } from '../context/UserContext';
import { isAdmin, isModerator } from '../admin/utils/roles';
import HrnbCard from '../components/controls/HrnbCard';
import HrnbButton from '../components/controls/HrnbButton';
import HrnbLoader from '../components/controls/HrnbLoader';
import HrnbAlert from '../components/controls/HrnbAlert';
import HrnbTextArea from '../components/controls/HrnbTextArea';
import HrnbSelect from '../components/controls/HrnbSelect';
import HrnbModal from '../components/controls/HrnbModal';
import { BackIcon, DeleteIcon, SendIcon, ClearIcon } from '../icons';
import FileUploader from '../components/forum/FileUploader';
import AttachmentList from '../components/forum/AttachmentList';
import '../css/pages/PostDetailPage.css';

export default function PostDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [newComment, setNewComment] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const [liked, setLiked] = useState(false);
    const [likes, setLikes] = useState(0);
    const [authorProfileId, setAuthorProfileId] = useState(null);
    const [commentFiles, setCommentFiles] = useState([]);

    // Состояние для удаления поста
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Состояние для удаления комментария
    const [deletingCommentId, setDeletingCommentId] = useState(null);

    // Состояние для загрузки файлов к посту (при редактировании)
    const [postFiles, setPostFiles] = useState([]);

    useEffect(() => {
        if (!currentUser) return;
        const pid = currentUser.profiles?.[0]?.id;
        if (pid) {
            setAuthorProfileId(pid);
        }
    }, [currentUser]);

    const fetchPost = useCallback(async () => {
        try {
            setLoading(true);
            const data = await forumApi.getPost(id);
            setPost(data);
            setLiked(data.liked || false);
            setLikes(data.likeCount ?? data.likes ?? 0);
            setError(null);
        } catch (err) {
            console.error('[PostDetailPage] Failed to fetch post:', err.message);
            setError('Не удалось загрузить пост');
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchComments = useCallback(async () => {
        try {
            const data = await forumApi.getPostComments(id);
            setComments(data || []);
        } catch (err) {
            console.error('[PostDetailPage] Failed to fetch comments:', err.message);
        }
    }, [id]);

    useEffect(() => {
        fetchPost();
        fetchComments();
    }, [id, fetchPost, fetchComments]);

    const handleAddComment = async () => {
        if (!newComment.trim() && commentFiles.length === 0) {
            setError('Введите текст комментария или прикрепите файл');
            return;
        }
        if (!authorProfileId) {
            setError('Не удалось определить профиль автора. Попробуйте позже.');
            return;
        }

        try {
            setCommentLoading(true);
            const attachmentIds = commentFiles.map(f => f.id);

            await forumApi.addCommentToPost(id, {
                content: newComment.trim(),
                postId: id,
                authorId: authorProfileId,
                attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
            });
            setNewComment('');
            setCommentFiles([]);
            await fetchComments();
        } catch (err) {
            console.error('[PostDetailPage] Failed to add comment:', err.message);
            setError('Не удалось добавить комментарий');
        } finally {
            setCommentLoading(false);
        }
    };

    const handleLike = async () => {
        if (!currentUser) {
            setError('Необходимо авторизоваться для лайка');
            return;
        }
        try {
            if (liked) {
                await forumApi.unlikePost(id);
                setLikes(prev => prev - 1);
            } else {
                await forumApi.likePost(id);
                setLikes(prev => prev + 1);
            }
            setLiked(!liked);
        } catch (err) {
            console.error('[PostDetailPage] Like failed:', err.message);
            setError('Не удалось поставить лайк');
        }
    };

    const handleCommentLike = async (comment) => {
        if (!currentUser) {
            setError('Необходимо авторизоваться для лайка');
            return;
        }
        try {
            if (comment.liked) {
                await forumApi.unlikeComment(comment.id);
            } else {
                await forumApi.likeComment(comment.id);
            }
            await fetchComments();
        } catch (err) {
            console.error('[PostDetailPage] Comment like failed:', err.message);
            setError('Не удалось поставить лайк комментарию');
        }
    };

    const handleCommentStatusChange = async (commentId, status) => {
        try {
            await forumApi.updateCommentStatus(commentId, status);
            await fetchComments();
        } catch (err) {
            console.error('[PostDetailPage] Failed to update comment status:', err.message);
            setError('Не удалось изменить статус комментария');
        }
    };

    const handleCommentFileUploaded = (fileData) => {
        setCommentFiles(prev => [...prev, fileData]);
    };

    const handleCommentFileRemoved = (fileId) => {
        setCommentFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleDeletePost = async () => {
        if (!currentUser) {
            setError('Необходимо авторизоваться');
            return;
        }

        const isAuthor = post.author?.id === authorProfileId || post.author?.userId === currentUser.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            setError('У вас нет прав для удаления этого поста');
            return;
        }

        try {
            setDeleting(true);
            await forumApi.deletePost(id);
            setDeleteModalOpen(false);
            navigate('/forum');
        } catch (err) {
            console.error('[PostDetailPage] Failed to delete post:', err.message);
            setError(err.response?.data?.message || 'Не удалось удалить пост');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!currentUser) {
            setError('Необходимо авторизоваться');
            return;
        }

        const comment = comments.find(c => c.id === commentId);
        const isAuthor = comment?.author?.id === authorProfileId || comment?.author?.userId === currentUser.id;
        const canDelete = isAuthor || isAdmin(currentUser) || isModerator(currentUser);

        if (!canDelete) {
            setError('У вас нет прав для удаления этого комментария');
            return;
        }

        try {
            setDeletingCommentId(commentId);
            await forumApi.deleteComment(commentId);
            await fetchComments();
        } catch (err) {
            console.error('[PostDetailPage] Failed to delete comment:', err.message);
            setError(err.response?.data?.message || 'Не удалось удалить комментарий');
        } finally {
            setDeletingCommentId(null);
        }
    };

    const handlePostFileUploaded = (fileData) => {
        setPostFiles(prev => [...prev, fileData]);
    };

    const handlePostFileRemoved = (fileId) => {
        setPostFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Не указано';
        const date = new Date(dateStr);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="post-detail-page">
                <HrnbLoader />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="post-detail-page">
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error || 'Пост не найден'}
                </HrnbAlert>
                <HrnbButton onClick={() => navigate('/forum')} className="mt-3" icon={<BackIcon />}>
                    Вернуться к списку
                </HrnbButton>
            </div>
        );
    }

    const {
        title,
        content,
        author,
        category,
        tags = [],
        createdAt,
        commentsCount = 0,
        viewCount = 0,
    } = post;

    return (
        <div className="post-detail-page">
            <HrnbButton variant="outline" onClick={() => navigate('/forum')} className="mb-3" icon={<BackIcon />}>
                Назад к списку
            </HrnbButton>

            <HrnbCard className="post-detail-card">
                <div className="post-detail-header">
                    <div className="post-detail-header-actions">
                        <h1 className="post-detail-title">{title}</h1>
                        <div className="post-detail-actions">
                            {(post.author?.id === authorProfileId || post.author?.userId === currentUser?.id || isAdmin(currentUser) || isModerator(currentUser)) && (
                                <HrnbButton
                                    variant="danger"
                                    size="sm"
                                    onClick={() => setDeleteModalOpen(true)}
                                    className="post-detail-delete-btn"
                                    icon={<DeleteIcon />}
                                >
                                    Удалить пост
                                </HrnbButton>
                            )}
                        </div>
                    </div>
                {category && (
                    <span className="post-detail-category">
                        {typeof category === 'string' ? category : (category.name || 'Категория')}
                    </span>
                )}
                </div>

                <div className="post-detail-meta">
                    {author && (
                        <span className="post-detail-author">
                            Автор: {typeof author === 'string' ? author : (author.displayName || 'Аноним')}
                        </span>
                    )}
                    {createdAt && (
                        <span className="post-detail-date">
                            Опубликовано: {formatDate(createdAt)}
                        </span>
                    )}
                </div>

                <div className="post-detail-content">
                    {content}
                </div>

                {post.attachments && post.attachments.length > 0 && (
                    <AttachmentList attachments={post.attachments} />
                )}

                {tags && tags.length > 0 && (
                    <div className="post-detail-tags">
                        {tags.map((tag, index) => (
                            <span key={tag.id || index} className="post-detail-tag">
                                #{typeof tag === 'string' ? tag : (tag.name || 'тег')}
                            </span>
                        ))}
                    </div>
                )}

                <div className="post-detail-stats">
                    <span className="post-detail-stat">
                        👁 {viewCount} просмотров
                    </span>
                    <span
                        className={`post-detail-stat post-detail-like-stat ${currentUser ? 'post-detail-like-clickable' : ''} ${liked ? 'post-detail-liked' : ''}`}
                        onClick={currentUser ? handleLike : undefined}
                        title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                    >
                        👍 {likes} {likes === 1 ? 'лайк' : likes < 5 ? 'лайка' : 'лайков'}
                    </span>
                    <span className="post-detail-stat">
                        💬 {commentsCount} комментариев
                    </span>
                </div>
            </HrnbCard>

            <div className="post-detail-comments">
                <h2 className="post-detail-comments-title">
                    Комментарии ({comments.length})
                </h2>

                {currentUser ? (
                    <div className="post-detail-comment-form">
                        <HrnbTextArea
                            label="Добавить комментарий"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Напишите ваш комментарий..."
                            rows={4}
                            disabled={commentLoading}
                        />
                        <FileUploader
                            onFileUploaded={handleCommentFileUploaded}
                            onFileRemoved={handleCommentFileRemoved}
                            uploadedFiles={commentFiles}
                            metadata="forum_attachment"
                            disabled={commentLoading}
                        />
                        <HrnbButton
                            variant="primary"
                            onClick={handleAddComment}
                            disabled={commentLoading || (!newComment.trim() && commentFiles.length === 0)}
                            icon={<SendIcon />}
                        >
                            {commentLoading ? <HrnbLoader size="sm" /> : 'Отправить'}
                        </HrnbButton>
                    </div>
                ) : (
                    <div className="post-detail-login-prompt">
                        <p>Войдите, чтобы оставить комментарий</p>
                    </div>
                )}

                <div className="post-detail-comments-list">
                    {comments.length > 0 ? (
                        comments.map((comment) => (
                            <HrnbCard key={comment.id} className={`comment-card ${comment.status === 'HIDDEN' ? 'comment-card-hidden' : ''} ${comment.status === 'DELETED' ? 'comment-card-deleted' : ''}`}>
                                <div className="comment-card-header">
                                    <span className="comment-card-author">
                                        {comment.author?.username || comment.author?.name || 'Аноним'}
                                    </span>
                                    <span className="comment-card-date">
                                        {formatDate(comment.createdAt)}
                                    </span>
                                    {comment.status && comment.status !== 'PUBLISHED' && (
                                        <span className={`comment-card-status comment-status-${comment.status.toLowerCase()}`}>
                                            {comment.status === 'HIDDEN' && 'Скрыт'}
                                            {comment.status === 'DELETED' && 'Удалён'}
                                        </span>
                                    )}
                                </div>
                                <div className="comment-card-content">
                                    {comment.status === 'DELETED' ? 'Комментарий удалён' : comment.content}
                                </div>
                                {comment.attachments && comment.attachments.length > 0 && (
                                    <AttachmentList attachments={comment.attachments} />
                                )}
                                <div className="comment-card-actions">
                                    <span
                                        className={`comment-card-like ${currentUser ? 'comment-card-like-clickable' : ''}`}
                                        onClick={currentUser ? () => handleCommentLike(comment) : undefined}
                                        title={currentUser ? 'Нажмите для лайка' : 'Войдите, чтобы поставить лайк'}
                                    >
                                        {comment.liked ? '❤️' : '🤍'} {comment.likeCount ?? comment.likes ?? 0}
                                    </span>
                                    <div className="comment-card-actions-right">
                                        {(comment.author?.id === authorProfileId || comment.author?.userId === currentUser?.id || isAdmin(currentUser) || isModerator(currentUser)) && comment.status !== 'DELETED' && (
                                            <HrnbButton
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDeleteComment(comment.id)}
                                                disabled={deletingCommentId === comment.id}
                                                className="comment-delete-btn"
                                                icon={deletingCommentId === comment.id ? null : <DeleteIcon />}
                                            >
                                                {deletingCommentId === comment.id ? <HrnbLoader size="sm" /> : 'Удалить'}
                                            </HrnbButton>
                                        )}
                                        {isModerator(currentUser) && comment.status !== 'DELETED' && (
                                            <div className="comment-moderation">
                                                <HrnbSelect
                                                    value={comment.status || 'PUBLISHED'}
                                                    onChange={(value) => handleCommentStatusChange(comment.id, value)}
                                                    options={[
                                                        { value: 'PUBLISHED', label: 'Опубликован' },
                                                        { value: 'HIDDEN', label: 'Скрыт' },
                                                        { value: 'DELETED', label: 'Удалён' },
                                                    ]}
                                                    size="sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </HrnbCard>
                        ))
                    ) : (
                        <p className="no-comments">Комментариев пока нет</p>
                    )}
                </div>
            </div>

            {/* Модальное окно подтверждения удаления поста */}
            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление поста"
                size="small"
            >
                <div className="delete-modal-content">
                    <p className="delete-modal-text">
                        Вы уверены, что хотите удалить этот пост? Это действие нельзя отменить.
                    </p>
                    <div className="delete-modal-actions">
                        <HrnbButton
                            variant="outline"
                            onClick={() => setDeleteModalOpen(false)}
                            disabled={deleting}
                            icon={<ClearIcon />}
                        >
                            Отмена
                        </HrnbButton>
                        <HrnbButton
                            variant="danger"
                            onClick={handleDeletePost}
                            disabled={deleting}
                            icon={<DeleteIcon />}
                        >
                            {deleting ? <HrnbLoader size="sm" /> : 'Удалить'}
                        </HrnbButton>
                    </div>
                </div>
            </HrnbModal>
        </div>
    );
}
